import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="scan"
export default class extends Controller {
  static targets = [
    "input",
    "reasonSelect",
    "merchantField",
    "merchantSelect",
    "returnedDateField",
    "notesField",
    "warrantyField",
    "costField",
    "vendorField",
    "vendorSelect",
    "conditionSelect",
    "conditionNotesSelect",
    "conditionNotesOtherField",
    "returnReasonSelect",
    "returnReasonOtherField",
    "decommissionNotesField",
    "decommissionNotesSelect",
    "decommissionNotesOtherField",
    "hardwareField",
    "inventoryFrame",
  ];

  static values = { inventoryUrl: String };

  connect() {
    this.inputTarget.focus();
    this.toggleFields();
    this.toggleOtherFields();
  }

  disconnect() {
    clearTimeout(this.lookUpTimeout);
  }

  toggleFields() {
    const reason = this.reasonSelectTarget.value;

    this.#toggle(
      "conditionSelect",
      !["new_stock", "merchant_return", "rma_return"].includes(reason),
    );
    this.#toggle("warrantyField", reason !== "new_stock");
    this.#toggle("costField", reason !== "new_stock");
    this.#toggle("vendorField", reason !== "new_stock" && reason !== "rma");
    this.#toggle(
      "merchantField",
      reason !== "deployment" && reason !== "merchant_return",
    );
    this.#toggle("returnedDateField", reason !== "merchant_return");
    this.#toggle("notesField", reason !== "merchant_return");
    this.#toggle("decommissionNotesField", reason !== "decommission");
    this.#toggleHardwareField();
  }

  lookUpInventory() {
    clearTimeout(this.lookUpTimeout);

    this.lookUpTimeout = setTimeout(() => {
      const url = new URL(this.inventoryUrlValue, window.location.origin);
      url.searchParams.set("serial_number", this.inputTarget.value.trim());
      this.inventoryFrameTarget.src = url.toString();
    }, 400);
  }

  applyInventoryLookup() {
    this.#toggleHardwareField();
    this.#autofillFromInventory();
  }

  // New stock always needs its hardware; a return only when the serial number
  // isn't in inventory yet, otherwise the item keeps the hardware it has.
  #toggleHardwareField() {
    const reason = this.reasonSelectTarget.value;
    const missing = this.#lookedUpInventory()?.dataset.inventory === "missing";

    this.#toggle(
      "hardwareField",
      reason !== "new_stock" &&
        !(["merchant_return", "rma_return"].includes(reason) && missing),
    );
  }

  #autofillFromInventory() {
    const reason = this.reasonSelectTarget.value;
    const { merchantId, vendorId } = this.#lookedUpInventory()?.dataset ?? {};

    if (reason === "merchant_return" && merchantId) {
      this.#setSelectValue(this.merchantSelectTarget, merchantId);
    } else if (reason === "rma" && vendorId) {
      this.#setSelectValue(this.vendorSelectTarget, vendorId);
    }
  }

  #lookedUpInventory() {
    return this.inventoryFrameTarget.querySelector("[data-inventory]");
  }

  #toggle(name, hidden) {
    const capitalized = name.charAt(0).toUpperCase() + name.slice(1);

    if (this[`has${capitalized}Target`]) {
      this[`${name}Target`].classList.toggle("d-none", hidden);
    }
  }

  #setSelectValue(select, id) {
    if (select.tomselect) {
      select.tomselect.setValue(String(id));
    } else {
      select.value = id;
    }
  }

  toggleOtherFields() {
    this.#toggleOtherField(
      this.conditionNotesSelectTarget,
      this.conditionNotesOtherFieldTarget,
    );
    this.#toggleOtherField(
      this.returnReasonSelectTarget,
      this.returnReasonOtherFieldTarget,
    );
    if (this.hasDecommissionNotesSelectTarget) {
      this.#toggleOtherField(
        this.decommissionNotesSelectTarget,
        this.decommissionNotesOtherFieldTarget,
      );
    }
  }

  #toggleOtherField(select, otherField) {
    otherField.classList.toggle("d-none", select.value !== "Other");
  }
}
